import React from "react";
import { usePayment, useTheme } from "../hooks";
import { ArrowRight } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CorporateService } from "../models/Service";
import { usePayMutation } from "../api/queries/usePayMutation";
import {
  getFailureDisplay,
  getStorageToken,
  isErrorResponse,
  isPayableAmount,
  payableAmount,
} from "../utils";
import { priceSearch, usePriceMutation } from "../api/queries";
import { useGlobalContext } from "../context/globalContext";
import { emailRegex } from "../constants";

/**
 * @deprecated Superseded by DepositFundTemplate, which owns the step sequence
 * this component's switch encodes (D-01). Kept compiling rather than deleted so
 * the way back from the rewrite is not a revert on a live payment path; removal
 * is TODO-245, homed in Phase 15.
 */
const PaymentActions = ({ service }: { service: CorporateService }) => {
  const { t } = useTranslation();
  const token = getStorageToken();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const payMutation = usePayMutation({ authorisation: token! });
  const priceMutation = usePriceMutation({ authorisation: token! });
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const {
    currentStep,
    setCurrentStep,
    amount,
    name,
    email,
    selectedMethod,
    phoneNumber,
    selectedWallet,
    customerUser,
    setError,
    setSubmittedOutcome,
  } = usePayment();
  const {
    setTotalAmount,
    setTotalFees,
    setInstrumentFee,
    setRegistrationFee,
    setCurrency,
  } = useGlobalContext();

  const isLoading = payMutation.isLoading || priceMutation.isLoading;

  // One decision, in place of the same ternary repeated at eight sites. It
  // takes either shape the handlers see: an onError handler holds the error,
  // an onSuccess handler holds the envelope itself. The `reference` the
  // resolver also returns is 16-12's to render on the status page.
  const showFailure = (failure: unknown) =>
    setError(getFailureDisplay(failure, t, currentLanguage).message);

  const isStepOneValid = () => {
    return (
      // The number the body will carry, not a truncating parse of the field.
      isPayableAmount(amount) &&
      (!service.is_searchable
        ? name.trim() !== "" &&
          email.trim() !== "" &&
          emailRegex.test(email.trim())
        : true) &&
      selectedMethod !== null &&
      phoneNumber.trim() !== "" &&
      phoneNumber.length === 9
    );
  };

  const handleContinue = () => {
    // The PRICE payload. It is NOT the pay body: D-HARM-3 and D-HARM-4 scope
    // to the pay request, so the price call keeps naming the service the way
    // it always has and the two are separated rather than sharing one literal
    // (TODO-266).
    const basePayload = {
      amount: payableAmount(amount),
      corporate_subsidiary_id: service?.corporate_subsidiary_id,
      payment_method_id: selectedMethod?.id!,
      corporate_service_id: service?.id,
    };

    // Everything the price payload carries, plus the one identifier the
    // harmonized pay route binds (D-HARM-13). The three retired fields ride
    // along deliberately -- see PaymentPayload.
    const payBase = {
      ...basePayload,
      core_service_id: service?.core_service_id,
    };

    switch (currentStep) {
      case 1: {
        if (!isStepOneValid()) return;

        if (!service?.is_searchable) {
          priceMutation.mutate(basePayload, {
            onSuccess: data => {
              if (isErrorResponse(data)) {
                showFailure(data);
                return;
              }
              setTotalFees(data.total_fees.toString());
              setTotalAmount(data.total_amount.toString());
              // Null-safe: the components only arrive once the CPS half
              // ships, and a bare toString would blank the screen.
              setInstrumentFee(String(data.instrument_fee ?? ""));
              setRegistrationFee(String(data.registration_fee ?? ""));
              setCurrentStep(2);
            },
            onError: (err: any) => showFailure(err),
          });
        }

        break;
      }

      case 2: {
        if (!service?.is_searchable) {
          payMutation.mutate(
            {
              ...payBase,
              channel: "App",
              instrument_account_owner: name,
              instrument_account_number: phoneNumber,
              notification_email: email,
            },
            {
              onSuccess: data => {
                // Unreachable: a pay response never has this legacy shape, and
                // a decline with an id resolves to its outcome page instead.
                // Left because 16-22 owns this file's flow branches.
                if (isErrorResponse(data)) {
                  showFailure(data);
                  return;
                }
                // The unit the payment was actually made in, kept beside the
                // amounts the outcome screen renders.
                setCurrency(data.currency);
                // The mutation used to navigate from inside api/, which was
                // the repository's only dependency-law violation. The caller
                // owns where the payer goes next.
                // Replaced and marked as sent: no Back re-offers this pay.
                const outcome = {
                  to: `transaction/${data.payment_item_id}?${priceSearch(basePayload)}`,
                  state: { phoneNumber },
                };
                setSubmittedOutcome(outcome);
                navigate(outcome.to, { replace: true, state: outcome.state });
              },
              onError: (err: any) => showFailure(err),
            }
          );
          return;
        }
        priceMutation.mutate(basePayload, {
          onSuccess: data => {
            if (isErrorResponse(data)) {
              showFailure(data);
              return;
            }
            setTotalFees(data.total_fees.toString());
            setTotalAmount(data.total_amount.toString());
            setInstrumentFee(String(data.instrument_fee ?? ""));
            setRegistrationFee(String(data.registration_fee ?? ""));
            setCurrentStep(3);
          },
          onError: (err: any) => showFailure(err),
        });
        break;
      }

      case 3: {
        payMutation.mutate(
          {
            ...payBase,
            customer_id: customerUser?.id,
            customer_wallet_id: selectedWallet?.id,
            instrument_account_number: phoneNumber || undefined,
            channel: "App",
          },
          {
            onSuccess: data => {
              if (isErrorResponse(data)) {
                showFailure(data);
                return;
              }
              setCurrency(data.currency);
              // Replaced and marked as sent: no Back re-offers this pay.
              const outcome = {
                to: `transaction/${data.payment_item_id}?${priceSearch(basePayload)}`,
                state: { phoneNumber },
              };
              setSubmittedOutcome(outcome);
              navigate(outcome.to, { replace: true, state: outcome.state });
            },
            onError: (err: any) => showFailure(err),
          }
        );
        break;
      }

      default:
        break;
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "white",
        border: 1,
        borderColor: theme?.color_primary || "grey.300",
        borderRadius: 2,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        p: 2,
        mb: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      <Button
        onClick={() => navigate(-1)}
        variant="outlined"
        disabled={isLoading}
        sx={{
          px: 3,
          py: 1.5,
          fontWeight: 500,
          textTransform: "none",
        }}
      >
        {t("payment.actions.cancel", "Retour")}
      </Button>

      <Box ml={currentStep === 1 ? "auto" : undefined}>
        {currentStep > 1 && (
          <Button
            onClick={handleBack}
            variant="outlined"
            disabled={isLoading}
            sx={{
              px: 3,
              py: 1.5,
              fontWeight: 500,
              textTransform: "none",
              borderColor: theme?.color_primary,
              color: theme?.color_primary,
              mr: 3,
            }}
          >
            {t("payment.actions.back", "Retour")}
          </Button>
        )}
        <Button
          onClick={handleContinue}
          disabled={
            ((currentStep === 2 || currentStep === 1) && !isStepOneValid()) ||
            isLoading
          }
          variant="contained"
          sx={{
            bgcolor: isStepOneValid() ? theme?.color_primary : "warning.main",
            color: isStepOneValid()
              ? "primary.contrastText"
              : "warning.contrastText",
            "&:hover": {
              bgcolor: isStepOneValid() ? theme?.color_primary : "warning.dark",
            },
            px: 3,
            py: 1.5,
            fontWeight: 500,
            textTransform: "none",
          }}
        >
          {isLoading ? (
            t("payment.actions.processing", "Traitement...")
          ) : (
            <>
              {currentStep === 1 ||
              (service?.is_searchable && currentStep === 2)
                ? t("payment.actions.continue", "Continuer")
                : t("payment.actions.confirm", "Confirmer le paiement")}
              <ArrowRight sx={{ ml: 1, fontSize: 20 }} />
            </>
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentActions;

